import { useCallback, useEffect, useState } from 'react';
import { PAGINATION } from '@erpgold/shared';
import {
  bankService,
  Bank,
  BankFilters,
  CreateBankDto,
  UpdateBankDto,
} from '../services/bank.service';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const useBanks = (initialFilters: BankFilters = {}) => {
  const [banks, setBanks] = useState<Bank[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<BankFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchBanks = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bankService.getBanks(filters);
      setBanks(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch banks'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks]);

  const createBank = async (data: CreateBankDto) => {
    const created = await bankService.createBank(data);
    await fetchBanks();
    return created;
  };

  const updateBank = async (id: string, data: UpdateBankDto) => {
    const updated = await bankService.updateBank(id, data);
    setBanks((prev) => prev.map((b) => (b.bank_id === id ? { ...b, ...updated } : b)));
    return updated;
  };

  const deleteBank = async (id: string) => {
    await bankService.deleteBank(id);
    setBanks((prev) => prev.filter((b) => b.bank_id !== id));
    setMeta((prev) => ({ ...prev, total: Math.max(0, prev.total - 1) }));
  };

  return {
    banks,
    loading,
    error,
    meta,
    filters,
    setFilters,
    refetch: fetchBanks,
    createBank,
    updateBank,
    deleteBank,
  };
};

export const useBankDetail = (id: string) => {
  const [bank, setBank] = useState<Bank | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchBank = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await bankService.getBankById(id);
      setBank(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch bank'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBank();
  }, [fetchBank]);

  return { bank, loading, error, refetch: fetchBank };
};
